import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { Wallet } from 'lucide-react';
import { useCreateDeposit, type DepositResult } from '@/api/billing-payments';
import { formatCurrency } from '@/lib/format-currency';
import { PaymentDialog } from './PaymentDialog';

// Preset top-up amounts (VND) — quick picks above the free-form field.
const PRESETS = [200_000, 500_000, 1_000_000, 5_000_000];
const MIN_AMOUNT = 1_000;

interface TopUpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Prefills the amount (e.g. the shortfall needed to cover a plan). */
  defaultAmount?: number;
  title?: string;
  description?: string;
  /** Fired after the pay dialog closes, so a caller can re-check the balance. */
  onClosed?: () => void;
}

/**
 * Self-serve top-up ("nạp tiền"): pick an amount → create a deposit invoice →
 * pay it (PayPal/bank) via the shared PaymentDialog. Paying the invoice credits
 * the wallet. Two internal steps so we never stack two dialogs at once.
 */
export function TopUpDialog({
  open,
  onOpenChange,
  defaultAmount,
  title = 'Top up wallet',
  description = 'Add funds to your account credit, then use it to pay for services.',
  onClosed,
}: TopUpDialogProps) {
  const [amount, setAmount] = useState<number>(defaultAmount ?? PRESETS[1]);
  const [deposit, setDeposit] = useState<DepositResult | null>(null);

  // Reset to the amount step whenever the dialog re-opens.
  useEffect(() => {
    if (open) {
      setDeposit(null);
      setAmount(defaultAmount ?? PRESETS[1]);
    }
  }, [open, defaultAmount]);

  const create = useCreateDeposit({
    onSuccess: (d) => setDeposit(d),
  });

  const valid = Number.isFinite(amount) && amount >= MIN_AMOUNT;

  // Once the deposit invoice exists, hand off to the pay dialog. Closing it
  // resets the flow and notifies the caller to re-check the wallet balance.
  if (deposit) {
    return (
      <PaymentDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            setDeposit(null);
            onOpenChange(false);
            onClosed?.();
          }
        }}
        invoiceId={deposit.invoiceId}
        amount={deposit.amount}
        customerId={deposit.customerId}
        createdAt={deposit.createdAt}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
              {PRESETS.map((p) => (
                <Button
                  key={p}
                  size="sm"
                  variant={amount === p ? 'primary' : 'secondary'}
                  onClick={() => setAmount(p)}
                >
                  {formatCurrency(p)}
                </Button>
              ))}
            </div>
            <label
              style={{
                display: 'grid',
                gap: 'var(--vn-space-1)',
                fontSize: 'var(--vn-font-size-sm)',
                color: 'var(--vn-fg-muted)',
              }}
            >
              Amount (VND)
              <Input
                type="number"
                inputMode="numeric"
                min={MIN_AMOUNT}
                step={1000}
                value={Number.isFinite(amount) ? String(amount) : ''}
                onChange={(e) => setAmount(Math.floor(Number(e.target.value)))}
              />
            </label>
            {create.isError && (
              <Alert tone="danger">Could not create the top-up invoice. Please try again.</Alert>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!valid || create.isPending}
            onClick={() => create.mutate({ amount })}
          >
            <Wallet size={15} />
            {create.isPending ? 'Creating…' : `Continue · ${formatCurrency(valid ? amount : 0)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
